import React from 'react';
import Icon from '../ui/Icon';
import Modal from '../ui/Modal';
import { PHONE_W, PHONE_H, PHONE_LARGE_SCALE } from './PhonePreview';
import phone from './PhonePreview.module.css';
import { substituteParams } from './templateParams.util';
import stylesSelf from './WhatsappPreview.module.css';

function findComponent(components, type) {
  return (components || []).find((c) => c.type === type);
}

// Real approved copy, fetched live from AWS End User Messaging Social (see
// whatsappTemplate.service.js) — components is Meta's own template shape:
// [{type: 'HEADER'|'BODY'|'FOOTER'|'BUTTONS', ...}].
function LiteralBody({ fetchedTemplate, params, imageUrl }) {
  const components = fetchedTemplate.components || [];
  const header = findComponent(components, 'HEADER');
  const body = findComponent(components, 'BODY');
  const footer = findComponent(components, 'FOOTER');
  const buttons = findComponent(components, 'BUTTONS');

  return (
    <>
      {header?.format === 'IMAGE' && (
        imageUrl
          ? <img src={imageUrl} alt="" className={stylesSelf.image} />
          // This template's approved HEADER is an image — reserve the space
          // even before one's uploaded, so it's visually obvious the message
          // will look incomplete without it (see WhatsappFields.jsx, which
          // also marks the upload field required in this case).
          : <div className={stylesSelf.imagePlaceholder}><Icon name="image" size={18} /></div>
      )}
      {header?.format === 'TEXT' && header.text && (
        <div className={stylesSelf.headerText}>{substituteParams(header.text, params)}</div>
      )}
      {body?.text && <div className={stylesSelf.bodyText}>{substituteParams(body.text, params)}</div>}
      {footer?.text && <div className={stylesSelf.footerText}>{footer.text}</div>}
      {buttons?.buttons?.length > 0 && (
        <div className={stylesSelf.buttons}>
          {buttons.buttons.map((b, i) => <div key={i} className={stylesSelf.buttonRow}>{b.text}</div>)}
        </div>
      )}
    </>
  );
}

// Structural fallback — only the name/params/image are stored in this app;
// shown until the real template is fetched, or if it can't be (not found,
// not yet approved, provider not configured).
function StructuralBody({ templateName, params, imageUrl }) {
  return (
    <>
      {imageUrl && <img src={imageUrl} alt="" className={stylesSelf.image} />}
      <div className={stylesSelf.tplName}>{templateName || 'template_name'}</div>
      {params.length > 0 && (
        <div className={stylesSelf.params}>
          {params.map((p, i) => (
            <div key={i} className={stylesSelf.paramRow}><span className={stylesSelf.paramIdx}>{`{{${i + 1}}}`}</span> {p}</div>
          ))}
        </div>
      )}
    </>
  );
}

const STATUS_NOTES = {
  not_found: "Couldn't find this template — check the name, or it's not approved yet.",
  error: "Couldn't fetch the live template — showing name/params only.",
};

function Frame({ templateName, templateParams, imageUrl, fetchedTemplate, fetchStatus, scale = 1 }) {
  const params = templateParams.split(',').map((p) => p.trim()).filter(Boolean);
  return (
    <div style={{ width: PHONE_W * scale, height: PHONE_H * scale }}>
      <div className={phone.phone} style={scale !== 1 ? { transform: `scale(${scale})`, transformOrigin: 'top left' } : undefined}>
        <div className={phone.notch} />
        <div className={stylesSelf.header}>
          <div className={stylesSelf.avatar}>P</div>
          <div className={stylesSelf.headerName}>PickMyWork</div>
        </div>
        <div className={stylesSelf.thread}>
          <div className={stylesSelf.bubble}>
            {fetchedTemplate
              ? <LiteralBody fetchedTemplate={fetchedTemplate} params={params} imageUrl={imageUrl} />
              : <StructuralBody templateName={templateName} params={params} imageUrl={imageUrl} />}
            {!fetchedTemplate && fetchStatus && STATUS_NOTES[fetchStatus] && (
              <div className={stylesSelf.statusNote}>{STATUS_NOTES[fetchStatus]}</div>
            )}
          </div>
        </div>
        {/* Covers whatever's already rendered (including a previous template's
            stale content) while a fetch triggered by a dropdown change is in
            flight — otherwise switching templates looked like nothing was
            happening until the new content silently replaced the old. */}
        {fetchStatus === 'loading' && (
          <div className={phone.loadingOverlay}>
            <div className={phone.spinner} />
            <div className={phone.loadingText}>Fetching template…</div>
          </div>
        )}
      </div>
    </div>
  );
}

class WhatsappPreview extends React.Component {
  constructor(props) {
    super(props);
    this.state = { expanded: false };
  }

  render() {
    const { templateName = '', templateParams = '', imageUrl = '', fetchedTemplate = null, fetchStatus = 'idle' } = this.props;
    const { expanded } = this.state;
    const captionSuffix = fetchedTemplate ? 'real approved copy' : 'not the final approved copy';

    return (
      <div className={phone.wrap}>
        <div className={phone.labelRow}>
          <span className={phone.label}>Live Preview</span>
          <button type="button" className={phone.expandBtn} title="Enlarge preview" onClick={() => this.setState({ expanded: true })}>
            <Icon name="open_in_full" size={11} />
          </button>
        </div>
        <Frame templateName={templateName} templateParams={templateParams} imageUrl={imageUrl} fetchedTemplate={fetchedTemplate} fetchStatus={fetchStatus} />
        <div className={phone.caption}>WhatsApp · {captionSuffix}</div>

        <Modal open={expanded} onClose={() => this.setState({ expanded: false })} title="WhatsApp — full preview" width="fit-content">
          <Frame templateName={templateName} templateParams={templateParams} imageUrl={imageUrl} fetchedTemplate={fetchedTemplate} fetchStatus={fetchStatus} scale={PHONE_LARGE_SCALE} />
        </Modal>
      </div>
    );
  }
}

export default WhatsappPreview;
